/**
 * The Pro Setup focus-areas catalog (#361) — ONE definition, three consumers.
 *
 *   ProSetupFlowModal   renders the pills and writes user_preferences.focus_areas
 *   TasksTab            reorders the "All of Kin's helpers" sheet from it
 *   focusAreaOrdering   tests the ordering contract against the real catalog
 *
 * It lived in two hand-synced copies until Copilot pointed out on PR #476 that
 * a renamed task_type would desync storage from display silently — the sheet
 * would just quietly stop hoisting one helper, with nothing failing. Adding a
 * grouping now means editing one array.
 *
 * KEYS ARE STORED IN THE DATABASE. Renaming one orphans every row that holds
 * it (the reader ignores unknown keys rather than crashing, so the failure is
 * silent). Labels are display-only and safe to reword.
 */

export type FocusArea = {
  /** Stored in user_preferences.focus_areas. Never rename without a backfill. */
  key: string;
  emoji: string;
  /** Display only. Matches the helpers-sheet wording so she sees one vocabulary. */
  label: string;
  /** scheduled_tasks.task_type values this grouping covers. */
  types: string[];
};

/**
 * Six groupings, not one pill per task type. A mom three minutes into setup is
 * saying what KIND of help she wants, not browsing a catalog — six taps cover
 * 11 of the 19 helpers in the sheet.
 *
 * The other 8 (event_finder, hobby_nudge, invite, prescription_refill,
 * reminder_nudge, snack_mom, ticket_watch, weekly_wins) are deliberately
 * uncovered: they're either one-off or too specific to guess at from a
 * six-pill card. They keep their curated position and stay fully reachable —
 * orderSectionsByFocus only reorders, never filters.
 *
 * This array's order is load-bearing twice over: it's the order the pills
 * render in, and selections are normalized to it on save, so the hoisted
 * helpers section mirrors the card she just filled out.
 */
export const FOCUS_AREAS: FocusArea[] = [
  { key: "meal_plan",   emoji: "🍽️", label: "Weekly Meal Plan",    types: ["meal_plan"] },
  { key: "restock",     emoji: "📦", label: "Restock Reminders",   types: ["product_reorder"] },
  { key: "kids",        emoji: "👶", label: "Kids & Appointments", types: ["size_check", "appointment_tracker"] },
  { key: "time_for_me", emoji: "💜", label: "Time for Me",         types: ["self_care", "date_night"] },
  { key: "birthdays",   emoji: "🎂", label: "Birthdays & Friends", types: ["birthday_reminder", "friend_checkin"] },
  { key: "home_pets",   emoji: "🏠", label: "Home & Pets",         types: ["home_maintenance", "pet_tracker", "plant_care"] },
];

/** key -> task types. Derived, so it can't drift from the catalog above. */
export const FOCUS_AREA_TYPES: Record<string, string[]> = Object.fromEntries(
  FOCUS_AREAS.map((a) => [a.key, a.types]),
);

/**
 * Selected keys in catalog order — what gets written to the column.
 *
 * Deliberately NOT tap order. Tapping six pills isn't ranking them; she may
 * well go top to bottom. Reading tap order as priority would invent intent the
 * interaction never expressed, and would make the helpers sheet shuffle
 * between sessions for no reason she could perceive.
 */
export function toStoredOrder(selectedKeys: string[]): string[] {
  return FOCUS_AREAS.filter((a) => selectedKeys.includes(a.key)).map((a) => a.key);
}

/**
 * The task types behind a stored focus_areas value, in catalog order.
 *
 * Unknown keys are skipped, not thrown on: a key retired from the card can
 * still be sitting on an old preference row, and a mom's helpers sheet must
 * not break because we reorganized the setup flow.
 */
export function focusTypesFor(focusAreas: string[] | null | undefined): string[] {
  return (focusAreas ?? []).flatMap((key) => FOCUS_AREA_TYPES[key] ?? []);
}

/**
 * Hoist the helpers she picked to the top of the "All of Kin's helpers" sheet.
 *
 * The section shape is structural, not TasksTab-specific, so this stays generic
 * over `{ header, items[] }` — it lives here rather than inline in an 11k-line
 * component so the ordering contract can actually be tested.
 *
 * Two invariants the tests pin:
 *   - null/empty focus_areas returns the input untouched. That's every account
 *     that predates the card, and their sheet must not move.
 *   - no helper is ever dropped. Hoisting reorders; it never filters.
 *
 * Within the hoisted section, helpers appear in CATALOG order — the same order
 * the setup card showed her — not in the order the curated sections happened to
 * list them. See toStoredOrder() for why that isn't a priority ranking.
 */
export function orderSectionsByFocus<T extends { type: string }>(
  sections: Array<{ header: string; items: T[] }>,
  focusAreas: string[] | null | undefined,
): Array<{ header: string; items: T[] }> {
  const focusTypes = focusTypesFor(focusAreas);
  const focusSet = new Set(focusTypes);
  if (focusSet.size === 0) return sections;

  const hoisted = sections
    .flatMap((sec) => sec.items)
    .filter((it) => focusSet.has(it.type))
    .sort((a, b) => focusTypes.indexOf(a.type) - focusTypes.indexOf(b.type));

  // A focus area whose types are all absent from the sheet must not leave an
  // empty "Based on your setup" header behind.
  if (hoisted.length === 0) return sections;

  return [
    { header: "✨ Based on your setup", items: hoisted },
    ...sections
      .map((sec) => ({ ...sec, items: sec.items.filter((it) => !focusSet.has(it.type)) }))
      .filter((sec) => sec.items.length > 0),
  ];
}
